import { CommonService } from './../../services/common.service';
import { UserService } from 'src/services/user.service';
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TitleService } from 'src/services/title.service';
import { AppNav } from 'src/routes/app-nav';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { filter, map, pluck, switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { EngagementDTO } from 'src/interfaces/engagementDTO.interface';
import { EngagementService } from 'src/services/engagement.service';
import Helpers from 'src/shared/helpers';
import { ApiResponse } from 'src/interfaces/apiresponse.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { UserDTO } from 'src/classes/userDTO';
import { QueryService } from '@/services/query.service';
import { PutQueryReqDTO } from '@/interfaces/putQueryReqDTO.interface';
import { FileUploaderComponent } from '@/components/fileuploader/fileuploader.component';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-query-invoices-page',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './query-invoices-page.component.html',
})
export class QueryInvoicesPageComponent implements OnInit, OnDestroy {
  @ViewChild(FileUploaderComponent) uploader!: FileUploaderComponent;

  private destroyed = new Subject<any>();
  private helpers: Helpers;

  messageKey: string = 'global-error-slot';
  putQueryReqDTO!: PutQueryReqDTO;
  engagements: EngagementDTO[] = [];
  invoiceDirectionSelectOptions: any[] = [];
  selectedVatNumber?: string;
  vatNumbers: string[] = [];
  selectedEngagement!: EngagementDTO;
  isValidVatNumber?: boolean;
  visibilityToggle: boolean = false;
  loading: boolean = false;
  submitEnabled: boolean = true;
  isAdmin?: boolean = false;
  user?: UserDTO;
  isVatAutomatic? = false;
  statusMonitorPageRoute: string = AppNav.StatusMonitor.route;
  invoiceDetailsInvoiceNumbersText: string = '';
  invoiceFilteredReportInvoiceNumbersText: string = '';

  disabledDatePicker = [{
    start: new Date(1900, 1, 2),
    end: new Date(1900, 1, 1),
  }];

  manualDatePicker = [{
    start: new Date(2020, 1, 1),
    end: new Date(),
  }];

  dateValidatorDTO: any [] = { ...this.disabledDatePicker };

  constructor(
    private titleService: TitleService,
    private engagementService: EngagementService,
    private confirmationService: ConfirmationService,
    private commonService: CommonService,
    private translocoService: TranslocoService,
    private messageService: MessageService,
    private userService: UserService,
    private queryService: QueryService
  ) {
    this.helpers = Helpers.getInstance(this.translocoService);
  }

  ngOnInit(): void {
    this.titleService.setTitle(AppNav.QueryInvoices.title);

    this.resetSelectedEngagement();
    this.setNewModel();
    this.setInvoiceDirectionSelectOptions();
    this.loadEngagements();

    this.userService.getLoggedInUser().subscribe((x) => (this.user = x));
    this.isAdmin = this.user?.userRoles?.includes('Admin') ?? false;

    this.translocoService.events$
      .pipe(
        filter((e) => e.type === 'translationLoadSuccess'),
        pluck('payload'),
        takeUntil(this.destroyed)
      )
      .subscribe(({}) => {
        this.setInvoiceDirectionSelectOptions();
      });
  }

  loadEngagements() {
    this.loading = true;
    this.engagementService.getAllDTO().subscribe({
      next: (engagements: EngagementDTO[]) => {
        engagements = engagements.map((x) => ({
          ...x,
          searchField: `${x.name} ${x.jobCode} ${x.vatNumbersWithDownloadType
            ?.map((r) => r.vatnumber)
            ?.join(' ')}`,
        }));

        this.engagements = engagements;
        this.loading = false;
      },
      error: (response: HttpErrorResponse) => {
        this.loading = false;
        console.log(response.error);
      },
    });
  }

  invoiceDetailsOnChange(event: any) {
    if (event.checked) {
      this.resetExceptInvoiceDetails();
    }
  }

  setInvoiceDirectionSelectOptions() {
    this.invoiceDirectionSelectOptions = [
      { name: this.translocoService.translate('incoming'), key: 'incoming' },
      { name: this.translocoService.translate('outgoing'), key: 'outgoing' },
    ];
  }

  resetSelectedEngagement() {
    this.selectedEngagement = {
      name: undefined,
      jobCode: undefined,
      vatNumbersWithDownloadType: [],
      searchField: undefined,
    } as EngagementDTO;
  }

  setNewModel() {
    this.putQueryReqDTO = {
      EngagementId: undefined,
      TechnicalUserId: undefined,
      PowerBiRequested: false,
      PowerBiDatesString: [],
      invoiceSelected: false,
      InvoiceFilteredReportRequested: false,
      InvoiceFilteredReportInvoiceNumbers: [],
      InvoiceDetailsRequested: false,
      InvoiceDetailsInvoiceNumbers: [],
      InvoiceDetailsInvoiceDirection: undefined,
      DatasetReportRequested: false,
      DatasetReportInvoiceDirection: undefined,
      DatasetReportDatesString: [],
      File: undefined,
      InvoiceDetailsFile: undefined,
    };
    this.invoiceDetailsInvoiceNumbersText = '';
    this.invoiceFilteredReportInvoiceNumbersText = '';
  }

  resetExceptInvoiceDetails() {
    this.putQueryReqDTO.EngagementId = undefined;
    this.putQueryReqDTO.TechnicalUserId = undefined;
    this.putQueryReqDTO.PowerBiRequested = false;
    this.putQueryReqDTO.PowerBiDatesString = [];
    this.putQueryReqDTO.invoiceSelected = false;
    this.putQueryReqDTO.InvoiceFilteredReportRequested = false;
    this.putQueryReqDTO.InvoiceFilteredReportInvoiceNumbers = [];
    this.putQueryReqDTO.DatasetReportRequested = false;
    this.putQueryReqDTO.DatasetReportInvoiceDirection = undefined;
    this.putQueryReqDTO.DatasetReportDatesString = [];
    this.putQueryReqDTO.File = undefined;
    this.invoiceFilteredReportInvoiceNumbersText = '';
  }

  onInvoiceDetailsInvoiceNumbersChange(value: string) {
    this.invoiceDetailsInvoiceNumbersText = value;
    this.putQueryReqDTO.InvoiceDetailsInvoiceNumbers =
      this.parseInvoiceNumbers(value);
  }

  onInvoiceFilteredReportInvoiceNumbersChange(value: string) {
    this.invoiceFilteredReportInvoiceNumbersText = value;
    this.putQueryReqDTO.InvoiceFilteredReportInvoiceNumbers =
      this.parseInvoiceNumbers(value);
  }

  private parseInvoiceNumbers(value: string | null | undefined): string[] {
    return (value ?? '')
      .split(/[\s,;]+/)
      .map((invoiceNumber) => invoiceNumber.trim())
      .filter(Boolean);
  }

  confirmUpload(data: [event: any, uploader: FileUpload]) {
    this.putQueryReqDTO.File = data[0].files[0];
  }

  confirmUploadInvoiceDetails(data: [event: any, uploader: FileUpload]) {
    this.putQueryReqDTO.InvoiceDetailsFile = data[0].files[0];
  }

  onEngagementNameListboxChange(event: any) {
    this.isValidVatNumber = undefined;
    this.selectedVatNumber = undefined;
    this.setNewModel();
    if (!event.value) {
      this.resetSelectedEngagement();
      return;
    }
    this.selectedEngagement = event.value;
    this.vatNumbers = this.selectedEngagement.vatNumbersWithDownloadType.map(
      (r) => r.vatnumber
    );
  }

  onEngagementVatNumbersListboxChange(event: any) {
    this.messageService.clear();
    this.putQueryReqDTO.DatasetReportDatesString = [];
    this.putQueryReqDTO.PowerBiDatesString = [];
    this.putQueryReqDTO.DatasetReportRequested = false;
    this.putQueryReqDTO.PowerBiRequested = false;
    const vatNumber: string = event.value;
    this.isValidVatNumber = undefined;

    this.isVatAutomatic =
      this.selectedEngagement.vatNumbersWithDownloadType.find(
        (r) => r.vatnumber === vatNumber
      )?.isAutomatic;
    if (this.isVatAutomatic) {
      this.isValidVatNumber = true;
      this.queryService.getTechnicalAvailableDates(vatNumber).subscribe((r) => {
        if (
          r &&
          r.minDate &&
          r.maxDate.getTime() &&
          r.minDate &&
          r.maxDate.getTime()
        )
          this.dateValidatorDTO = [{start: r.minDate, end: r.maxDate}];
        else this.dateValidatorDTO = { ...this.disabledDatePicker };
      });
      return;
    }

    this.queryService
      .validateTechnicalUserByVatNumber(vatNumber)

      .subscribe({
        next: (response: ApiResponse) => {
          this.helpers.showApiResponseMessage(
            response,
            this.messageService,
            this.messageKey
          );
          this.isValidVatNumber = response.data;

          this.dateValidatorDTO = { ...this.manualDatePicker };
        },
        error: (response: HttpErrorResponse) => {
          this.helpers.showApiResponseMessage(
            response,
            this.messageService,
            this.messageKey
          );
          this.isValidVatNumber = false;
        },
      });
  }

  showSubmitConfirm() {
    if (!this.validateModel()) {
      console.log('validation error');
      return;
    }

    const confirmObj: Confirmation = {
      ...Helpers.getInstance(this.translocoService).getConfirmBase(
        'defaultConfirmWithCheckbox'
      ),
      header: this.translocoService.translate('confirm'),
      message: this.translocoService.translate('confirm_query_invoices'),
      acceptVisible: true,
      rejectVisible: true,
      closeOnEscape: true,
      accept: async () => {
        this.putQueryReqDTO!.TechnicalUserId =
          this.selectedEngagement.vatNumbersWithDownloadType.find(
            (r) => r.vatnumber === this.selectedVatNumber
          )?.technicalUserId;
        this.runAudit();
      },
    };

    this.confirmationService.confirm(confirmObj);
  }

  runAudit() {
    this.messageService.clear();
    this.submitEnabled = false;
    this.putQueryReqDTO.EngagementId = this.selectedEngagement.id?.toString();
    this.queryService.putQuery(this.putQueryReqDTO).subscribe({
      next: (response: ApiResponse) => {
        this.messageService.add({
          key: this.messageKey,
          severity: 'success',
          summary: this.translocoService.translate('success'),
          detail: this.translocoService.translate(
            'invoice_query_initialize_success'
          ),
          life: 30000,
        });
      },
      error: (response: HttpErrorResponse) => {
        this.submitEnabled = true;

        this.messageService.add({
          key: this.messageKey,
          severity: 'error',
          summary: this.translocoService.translate('error'),
          detail: this.translocoService.translate('error_general'),
          life: 30000,
        });
      },
    });
  }

  reset() {
    this.isValidVatNumber = undefined;
    this.selectedVatNumber = undefined;
    this.resetSelectedEngagement();
    this.setNewModel();
    this.submitEnabled = true;

    // hack to reload the first listbox
    this.visibilityToggle = true;
    setTimeout(() => {
      this.visibilityToggle = false;
    }, 10);
  }

  validateModel(): boolean {
    if (
      !this.putQueryReqDTO.DatasetReportRequested &&
      !this.putQueryReqDTO.PowerBiRequested &&
      !this.putQueryReqDTO.InvoiceFilteredReportRequested &&
      !this.putQueryReqDTO.InvoiceDetailsRequested
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate('error_nothing_selected'),
        life: 30000,
      });
      return false;
    } else if (
      this.putQueryReqDTO.DatasetReportRequested &&
      (!this.putQueryReqDTO.DatasetReportDatesString ||
        this.putQueryReqDTO.DatasetReportDatesString.length < 2 ||
        this.putQueryReqDTO.DatasetReportDatesString[0] >
          this.putQueryReqDTO.DatasetReportDatesString[1])
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate('error_date_range'),
        life: 30000,
      });
      return false;
    } else if (
      this.putQueryReqDTO.DatasetReportRequested &&
      !this.putQueryReqDTO.DatasetReportInvoiceDirection
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate(
          'error_invoice_direction_missing'
        ),
        life: 30000,
      });
      return false;
    } else if (
      this.putQueryReqDTO.PowerBiRequested &&
      (!this.putQueryReqDTO.PowerBiDatesString ||
        this.putQueryReqDTO.PowerBiDatesString.length < 2 ||
        this.putQueryReqDTO.PowerBiDatesString[0] >
          this.putQueryReqDTO.PowerBiDatesString[1])
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate('error_bi_date_range'),
        life: 30000,
      });

      return false;
    } else if (
      this.putQueryReqDTO.InvoiceFilteredReportRequested &&
      (!this.putQueryReqDTO.InvoiceFilteredReportInvoiceNumbers ||
        !this.putQueryReqDTO.InvoiceFilteredReportInvoiceNumbers.length) &&
      !this.putQueryReqDTO.File
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate(
          'error_missing_invoice_numbers'
        ),
        life: 30000,
      });

      return false;
    } else if (
      this.putQueryReqDTO.InvoiceFilteredReportRequested &&
      this.putQueryReqDTO.InvoiceFilteredReportInvoiceNumbers &&
      this.putQueryReqDTO.InvoiceFilteredReportInvoiceNumbers.length &&
      this.putQueryReqDTO.File
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate(
          'error_invoice_numbers_and_file_duplicate'
        ),
        life: 30000,
      });

      return false;
    } else if (
      this.putQueryReqDTO.InvoiceDetailsRequested &&
      this.putQueryReqDTO.InvoiceDetailsInvoiceNumbers &&
      this.putQueryReqDTO.InvoiceDetailsInvoiceNumbers.length &&
      this.putQueryReqDTO.InvoiceDetailsFile
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate(
          'error_invoice_details_numbers_and_file_duplicate'
        ),
        life: 30000,
      });

      return false;
    } else if (
      this.putQueryReqDTO.InvoiceDetailsRequested &&
      !this.putQueryReqDTO.InvoiceDetailsInvoiceDirection
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate(
          'error_invoice_direction_missing'
        ),
        life: 30000,
      });

      return false;
    } else if (
      this.putQueryReqDTO.InvoiceDetailsRequested &&
      (!this.putQueryReqDTO.InvoiceDetailsInvoiceNumbers ||
        !this.putQueryReqDTO.InvoiceDetailsInvoiceNumbers.length) &&
      !this.putQueryReqDTO.InvoiceDetailsFile
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate(
          'error_missing_invoice_numbers'
        ),
        life: 30000,
      });

      return false;
    }

    return true;
  }

  downloadTemplate() {
    this.commonService.downloadTemplate('invoice');
  }

  ngOnDestroy(): void {
    this.destroyed.next(undefined);
    this.destroyed.complete();
  }
}
